import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import type { StoreView } from '@wodi/shared';
import { Button, EmptyState, LoadingBlock, Notice, PageHeader, Select } from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import { pharmacyStoresQuery } from '../api/pharmacy.ts';
import { screen } from '../layout/screen.tsx';
import { errorText } from '../stock/common.tsx';
import styles from './pharmacy.module.css';

const PHARMACY_STORE_KEY = 'wodi.pharmacy.store';

/** The pharmacy store last used on this device. Not patient data; a convenience only. */
function rememberedPharmacy(): string | null {
  try {
    return window.localStorage.getItem(PHARMACY_STORE_KEY);
  } catch {
    return null;
  }
}

function rememberPharmacy(storeId: string): void {
  try {
    window.localStorage.setItem(PHARMACY_STORE_KEY, storeId);
  } catch {
    // Private windows may refuse storage; the page works without it.
  }
}

/** The pharmacy's own pages, one click apart. */
export function PharmacyNav() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const location = useLocation();
  if (session === null) return null;
  const links = [
    { to: '/pharmacy', label: 'Prescriptions', show: can(session, 'pharmacy.read') },
    { to: '/pharmacy/counter', label: 'Counter sale', show: can(session, 'pharmacy.sell') },
    { to: '/pharmacy/returns', label: 'Returns', show: can(session, 'pharmacy.read') },
    { to: '/pharmacy/ward-orders', label: 'Ward orders', show: can(session, 'pharmacy.read') },
  ] as const;
  return (
    <nav aria-label="Pharmacy" className={screen.tabs}>
      {links
        .filter((l) => l.show)
        .map((l) => (
          <Link
            key={l.to}
            to={l.to}
            activeOptions={{ exact: true }}
            {...(location.pathname === l.to ? { 'aria-current': 'page' as const } : {})}
          >
            {l.label}
          </Link>
        ))}
    </nav>
  );
}

/**
 * Loads the facility's pharmacy stores and renders the page for the one in use on this
 * device, with a chooser when there are several. Handles loading, error and empty states.
 */
export function WithPharmacyStore({
  title,
  children,
}: {
  title: string;
  children: (store: StoreView) => ReactNode;
}) {
  const stores = useQuery(pharmacyStoresQuery);
  const [chosen, setChosen] = useState<string | null>(rememberedPharmacy);
  const pharmacies = (stores.data ?? []).filter(
    (s) => s.kind === 'pharmacy' && s.status === 'active',
  );
  const store = pharmacies.find((s) => s.id === chosen) ?? pharmacies[0];
  return (
    <div className={styles.page}>
      <PageHeader
        title={title}
        {...(pharmacies.length > 1 && store !== undefined
          ? {
              actions: (
                <Select
                  label="Pharmacy"
                  options={pharmacies.map((s) => ({ id: s.id, label: s.name }))}
                  value={store.id}
                  onChange={(id) => {
                    if (id === null) return;
                    setChosen(String(id));
                    rememberPharmacy(String(id));
                  }}
                />
              ),
            }
          : {})}
      />
      <PharmacyNav />
      {stores.isPending ? <LoadingBlock label="Loading the pharmacy" rows={3} /> : null}
      {stores.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void stores.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(stores.error, "The pharmacy couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {stores.isSuccess && store === undefined ? (
        <EmptyState>
          This facility has no pharmacy store switched on. Add one in Settings, under Stores.
        </EmptyState>
      ) : null}
      {store === undefined ? null : children(store)}
    </div>
  );
}
